import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { MojoFeatureExtensionsService } from 'mojo-feature-extensions';
import { checkPrivileges, MojoPrivilegesService } from 'mojo-privileges';
import { iif, of } from 'rxjs';
import {
  catchError,
  concatMap,
  filter,
  first,
  map,
  mergeMap,
  pluck,
  switchMap,
  withLatestFrom,
} from 'rxjs/operators';

import { AppFeatureExtensions } from '@app/app-feature-extensions.config';
import {
  associationManySuccessEffectPatchAssociationFilter,
  associationsGetList,
  associationsLoadSuccess,
} from '@app/collections/associations';
import { getJobsSelected, getJobsSelectedId } from '@app/collections/jobs';
import { LoadAggregatedJobsData } from '@collections/aggregations';
import { getCandidatesSelectedId } from '@collections/candidates';
import { DEFAULT_JOB_ID } from '@modules/job-page/store/job.reducer';
import { NoteSubject } from 'mojo-irp-notes';
import { NoteFactory } from '@modules/notes/note-factory/note-factory';
import { notesCreate } from '@modules/notes/store';
import { DangerAlert, SuccessAlert } from '@shared/alert';
import { getCandidatePageKey } from '@shared/candidate-filters/candidate-filters.model';
import { Association, Candidate, Job, AssociationStatusConfiguration } from '@shared/models';
import { Pagination } from '@shared/pagination';
import {
  AssociationConfigurationService,
  AssociationService,
  JobService,
  SearchCandidatesService,
} from '@shared/services';
import {
  isTruthy,
  PROSPECTS_LABEL,
  WAITING_FOR_APPLICATION_STATUS,
  PROSPECT_STATUS,
} from '@shared/utils';

import { AssignConfig } from '../assign.abstract.config';
import {
  assignCandidatesList,
  assignCandidatesLoad,
  assignCandidatesLoadFail,
  assignCandidatesLoadSuccess,
  assignedRejectedCandidatesLoad,
  assignedRejectedCandidatesLoadFail,
  assignRejectedCandidatesLoadSuccess,
  associateManyJobs,
  associateManyJobsFail,
  associateManyJobsSuccess,
  associationsList,
  associationStatusConfigurationsLoad,
  getJobAssociationStatusConfigurations,
  associationStatusConfigurationsLoadFail,
  associationStatusConfigurationsLoadSuccess,
  associateCandidates,
  associateCandidatesSuccess,
  setTotalNumberOfCandidatesAndProspects,
  loadTotalElements,
} from './assigns.actions';
import {
  getAssociationStatusConfigurationsShortList,
  getFiltersSetOnAssignedCandidates,
  getFlattenAssociationStatusConfigurations,
  getAssociationStatusConfigurationsForJobs,
  getAssociationStatusConfigurationsReady,
  getHMFiltersSetOnAssignedCandidates,
} from './assigns.selectors';
import { loadCandidateHiringManagerStatus } from '@collections/hiring-manager-status';
import { candidatesExtraInfoLoad } from '@modules/candidate-extra-info/store/candidate-extra-info.actions';

// TODO: double check if effects run by this class are not duplicating actions
// as this class is instatiated multiple times
@Injectable()
export class AssignsEffects {
  associationForManyJobs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(associateManyJobs, associateCandidates),
      mergeMap(({ associations, totalMatchesAssign, actions, batchOperation }) => {
        const safeActions = Array.isArray(actions) ? actions : [];

        return this.associationService.postAssociateMany(associations).pipe(
          concatMap((response: Association[]) => {
            const jobsAssociationsMap = new Map<number, Partial<Association>>();
            associations.forEach((job) => jobsAssociationsMap.set(job.reqId, { ...job }));
            const tempAssociations = response.map(
              (association) =>
                ({
                  ...association,
                  noteComment: jobsAssociationsMap.get(association.reqId)?.noteComment,
                  hasNote: jobsAssociationsMap.get(association.reqId)?.hasNote,
                } as Association),
            );

            if (batchOperation) {
              safeActions.push(
                associateCandidatesSuccess({
                  associations: tempAssociations,
                  totalMatchesAssign,
                }),
              );
            } else {
              safeActions.push(associateManyJobsSuccess({ associations: tempAssociations }));
            }

            return safeActions;
          }),
          catchError((error) => {
            const associationsSize = associations.length;
            return of(associateManyJobsFail({ error, associationsSize }));
          }),
        );
      }),
    ),
  );

  associateManyJobsFail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(associateManyJobsFail),
      map(() => new DangerAlert('Something went wrong, try refreshing the page.')),
    ),
  );

  associationSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(associateCandidatesSuccess),
      switchMap((payload) =>
        of(payload).pipe(
          withLatestFrom(
            this.store.select(getFiltersSetOnAssignedCandidates),
            this.store.select(getHMFiltersSetOnAssignedCandidates),
            this.store.select(getFlattenAssociationStatusConfigurations, {
              jobId: payload.associations[0].reqId,
            }),
            this.featureExtensionService.hasFeatureExtension(AppFeatureExtensions.GREENHOUSE_ATS),
          ),
        ),
      ),
      mergeMap(
        ([
          { associations, totalMatchesAssign },
          filters,
          hmFilters,
          statusConfiguration,
          isGreenhouse,
        ]) => {
          const actions = [];
          let label = this.getAssociationLabel(isGreenhouse, associations[0].status);

          if (!totalMatchesAssign) {
            const status = statusConfiguration.find(
              (config) => config.id === associations[0].status,
            );
            const subStatus = statusConfiguration.find(
              (config) => config.id === associations[0].subStatus,
            ) || {
              isRejectedState: false,
              label: '',
            };

            label =
              status.isRejectedState || subStatus.isRejectedState
                ? subStatus.label.includes('Withdrawn')
                  ? 'Candidate is withdrawn'
                  : 'Candidate has been rejected'
                : associations[0].status?.startsWith('Prospect')
                ? 'Candidate has become a prospect'
                : 'Candidate has been moved to ' + status.label;
          }

          if (associations[0].hasNote) {
            actions.push(
              ...associations.map((association) =>
                notesCreate({
                  payload: this.noteFactory.create(
                    association,
                    NoteSubject.Association,
                    statusConfiguration.find((config) => config.id === association.status)?.id,
                    statusConfiguration.find((config) => config.id === association.subStatus)?.id,
                  ),
                }),
              ),
            );
          }

          actions.push(assignCandidatesLoad({ filters, hmSelectedStatuses: hmFilters }));
          actions.push(
            LoadAggregatedJobsData({ jobsIds: [], updateSingleAggregatedJobData: true }),
          );

          return [...actions.filter(Boolean), new SuccessAlert(label)];
        },
      ),
    ),
  );

  private getAssociationLabel(isGreenhouse: boolean, status: string) {
    return isGreenhouse
      ? status?.startsWith('Prospect')
        ? 'Candidate has become a prospect'
        : 'Candidate has become a direct applicant'
      : 'Candidate has been moved to shortlist';
  }

  associationManySuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(associateManyJobsSuccess),
      pluck('associations'),
      switchMap((associations: Association[]) =>
        of(associations).pipe(
          withLatestFrom(
            this.store.select(getFiltersSetOnAssignedCandidates),
            this.store.select(getHMFiltersSetOnAssignedCandidates),
            this.store.select(getCandidatesSelectedId),
            this.store.select(getAssociationStatusConfigurationsForJobs, {
              jobIds: associations.map(({ reqId }) => reqId),
            }),
          ),
        ),
      ),
      mergeMap(
        ([associations, filters, hmFilters, candidateId, configurations]: [
          Association[],
          string[],
          string[],
          number,
          { [jobId: string]: AssociationStatusConfiguration[] },
        ]) => {
          const actions = [];
          actions.push(
            ...associations
              .filter((association) => association.hasNote)
              .map((association) =>
                notesCreate({
                  payload: this.noteFactory.create(
                    association,
                    NoteSubject.Association,
                    configurations[association.reqId].find(
                      (item) => item.label === association.status,
                    )?.id,
                  ),
                }),
              ),
          );

          const associationAction = candidateId
            ? associationManySuccessEffectPatchAssociationFilter({
                id: getCandidatePageKey(candidateId),
              })
            : null;

          actions.push(assignCandidatesLoad({ filters, hmSelectedStatuses: hmFilters }));
          actions.push(
            assignedRejectedCandidatesLoad({
              params: { offset: 0 },
            }),
          );

          return [...actions, associationAction].filter(Boolean);
        },
      ),
    ),
  );

  loadAssignCandidates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(assignCandidatesLoad),
      switchMap((payload) =>
        this.store.select(getJobsSelected).pipe(
          filter((job) => !!job),
          switchMap((job) =>
            of(payload).pipe(
              withLatestFrom(
                of(job),
                this.store.select(getAssociationStatusConfigurationsShortList, { jobId: job.id }),
              ),
            ),
          ),
        ),
      ),
      switchMap(([payload, job, statusesConfiguration]) => {
        let statuses = [...(payload.filters || [])];

        const containsProspectTabStatuses = !!statuses.find((s) => s === PROSPECTS_LABEL);
        statuses = statusesConfiguration
          .filter((o1) => statuses.some((o2) => o1.label === o2))
          .map((obj) => obj.id);
        payload.selectedStatuses = statuses;

        if (containsProspectTabStatuses) {
          statuses.push(WAITING_FOR_APPLICATION_STATUS);
          statuses.push(PROSPECT_STATUS);
          payload.selectedStatuses = [];
        }
        const params = {
          ...payload.params,
          jobId: job.id,
          size: 10,
        };

        return this.service.shortlisted({ params }, statuses, payload.hmSelectedStatuses).pipe(
          switchMap((response) => [
            assignCandidatesLoadSuccess({ payload: response }),
            loadTotalElements({
              filters: payload.selectedStatuses,
              params,
              hmFilters: payload.hmSelectedStatuses,
            }),
          ]),
          catchError((error) => of(assignCandidatesLoadFail({ error }))),
        );
      }),
    ),
  );

  loadTotalAssignCandidates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadTotalElements),
      withLatestFrom(
        this.featureExtensionService.hasFeatureExtension(AppFeatureExtensions.FMI_INVITE_TO_APPLY),
      ),
      switchMap(([payload, hasInviteToApply]) => {
        let statuses = [...(payload.filters || [])];

        if (statuses[0] === 'All statuses') statuses = [];

        return this.service
          .shortlisted({ params: payload.params }, statuses, payload.hmFilters)
          .pipe(
            switchMap((allCandidates) => {
              statuses = [];
              if (hasInviteToApply) {
                statuses.push(WAITING_FOR_APPLICATION_STATUS);
              }
              statuses.push(PROSPECT_STATUS);

              return this.service
                .shortlisted({ params: payload.params }, statuses, payload.hmFilters)
                .pipe(
                  switchMap((responseProspect) =>
                    of(
                      setTotalNumberOfCandidatesAndProspects({
                        totalAllCandidates: allCandidates.totalElements,
                        totalProspects: responseProspect.totalElements,
                      }),
                    ),
                  ),
                );
            }),
            catchError((error) => of(assignCandidatesLoadFail({ error }))),
          );
      }),
    ),
  );

  loadAssignRejectedCandidates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(assignedRejectedCandidatesLoad),
      switchMap((payload) =>
        this.store
          .select(getJobsSelected)
          .pipe(
            filter(isTruthy),
            switchMap(({ id }: Job) => {
              const params = {
                ...payload.params,
                jobId: id,
                size: 10,
              };
              return this.service.rejected({ params }).pipe(
                map((payload) => assignRejectedCandidatesLoadSuccess({ payload })),
                catchError((error) => of(assignedRejectedCandidatesLoadFail({ error }))),
              );
            }),
          )
          .pipe(first()),
      ),
    ),
  );

  assignsCandidatesLoadSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(assignRejectedCandidatesLoadSuccess, assignCandidatesList),
      withLatestFrom(this.store.select(getJobsSelectedId)),
      switchMap(([, jobId]) => of(associationsGetList({ jobId }))),
    ),
  );

  assignRejectedCandidatesLoadSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(assignRejectedCandidatesLoadSuccess),
      withLatestFrom(this.store.select(getJobsSelectedId)),
      switchMap(([{ payload }, jobId]) =>
        of(payload).pipe(
          withLatestFrom(
            this.featureExtensionService.hasFeatureExtension(
              AppFeatureExtensions.HIRING_MANAGER_FLOW,
            ),
          ),
          filter(([_, enabled]) => enabled),
          map(([response, _]) =>
            loadCandidateHiringManagerStatus({
              jobId,
              candidatesIds: response.content.map(({ id }) => id),
            }),
          ),
        ),
      ),
    ),
  );

  loadAssignCandidatesSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(assignCandidatesLoadSuccess),
      pluck('payload'),
      checkPrivileges(
        [this.assignConfig.privileges.assigned.candidates],
        this.privilegesService.userPrivileges$,
      ),
      switchMap((payload: Pagination<Candidate>) => [
        assignCandidatesList({ payload }),
        candidatesExtraInfoLoad({ candidateIds: payload.content?.map((item) => item.id) }),
      ]),
    ),
  );

  assignCandidatesList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(associationsLoadSuccess),
      map(({ associationPage }) => associationsList({ payload: associationPage })),
    ),
  );

  associationsListSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(associationsList),
      pluck('payload', 'content'),
      withLatestFrom(
        this.featureExtensionService.hasFeatureExtension(
          AppFeatureExtensions.ASSOCIATION_CONFIGURATIONS_PER_JOB,
        ),
      ),
      mergeMap(([associations, configurationsPerJob]) => {
        if (!configurationsPerJob) {
          return [associationStatusConfigurationsLoad({})];
        }
        return [
          ...associations.map(({ reqId }) => associationStatusConfigurationsLoad({ jobId: reqId })),
        ];
      }),
    ),
  );

  getJobAssociationStatusConfigurations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getJobAssociationStatusConfigurations),
      pluck('jobId'),
      mergeMap((id) =>
        iif(
          () => !!id,
          this.jobService.get({ url: `${id}/associations/configuration` }).pipe(
            map((configurations) =>
              associationStatusConfigurationsLoadSuccess({ jobId: id, configurations }),
            ),
            catchError((error) => of(associationStatusConfigurationsLoadFail({ error }))),
          ),
          this.associationConfigService.get({ url: 'configuration' }).pipe(
            map((configurations) =>
              associationStatusConfigurationsLoadSuccess({ jobId: DEFAULT_JOB_ID, configurations }),
            ),
            catchError((error) => of(associationStatusConfigurationsLoadFail({ error }))),
          ),
        ),
      ),
    ),
  );

  loadJobAssociationStatusConfigurations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(associationStatusConfigurationsLoad),
      switchMap((payload) =>
        of(payload).pipe(
          withLatestFrom(
            this.store.select(getAssociationStatusConfigurationsReady, { jobId: payload.jobId }),
          ),
        ),
      ),
      filter(([_, ready]) => !ready),
      map(([payload, _]) => getJobAssociationStatusConfigurations({ ...payload })),
    ),
  );

  constructor(
    private associationService: AssociationService,
    private associationConfigService: AssociationConfigurationService,
    private jobService: JobService,
    private actions$: Actions,
    private service: SearchCandidatesService,
    private store: Store<{}>,
    private noteFactory: NoteFactory,
    private privilegesService: MojoPrivilegesService,
    private featureExtensionService: MojoFeatureExtensionsService,
    private assignConfig: AssignConfig,
  ) {}
}
